import clsx from 'clsx';
import { Loader2, Star, Volume2 } from 'lucide-react';
import { forwardRef, useEffect, useState, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode } from 'react';
import { Link } from 'react-router';
import { playVoice, stopVoice } from '../lib/voice';

// ---------------------------------------------------------------------------
// Button
// ---------------------------------------------------------------------------
type Variant = 'primary' | 'secondary' | 'ghost' | 'gold';
type Size = 'md' | 'lg' | 'xl';

const variants: Record<Variant, string> = {
  primary: 'bg-brand text-white hover:bg-brand-strong shadow-[0_4px_0_0_var(--color-brand-strong)] active:translate-y-[2px] active:shadow-[0_2px_0_0_var(--color-brand-strong)]',
  gold: 'bg-gold text-ink hover:bg-[#f5b552] shadow-[0_4px_0_0_var(--color-gold-strong)] active:translate-y-[2px] active:shadow-[0_2px_0_0_var(--color-gold-strong)]',
  secondary: 'bg-surface text-ink border-2 border-line-strong hover:border-brand hover:text-brand',
  ghost: 'text-ink-soft hover:bg-brand-soft hover:text-brand',
};
const sizes: Record<Size, string> = {
  md: 'h-11 px-5 text-[15px] rounded-xl gap-2',
  lg: 'h-14 px-7 text-lg rounded-2xl gap-2.5',
  xl: 'h-16 px-9 text-xl rounded-2xl gap-3',
};

const base =
  'inline-flex select-none items-center justify-center font-display font-bold transition-[background,box-shadow,transform,color,border-color] duration-150 disabled:cursor-not-allowed disabled:opacity-55';

/** A router link that looks like a button (never nest a button inside a link). */
export function ButtonLink({
  to, variant = 'primary', size = 'md', icon, className, children, reloadDocument,
}: { to: string; variant?: Variant; size?: Size; icon?: ReactNode; className?: string; children: ReactNode; reloadDocument?: boolean }) {
  return (
    <Link to={to} reloadDocument={reloadDocument} className={clsx(base, variants[variant], sizes[size], className)}>
      {icon}
      {children}
    </Link>
  );
}

export const Button = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: Size; loading?: boolean; icon?: ReactNode }
>(function Button({ variant = 'primary', size = 'md', loading, icon, className, children, disabled, ...rest }, ref) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={clsx(
        base,
        variants[variant],
        sizes[size],
        className,
      )}
      {...rest}
    >
      {loading ? <Loader2 className="size-5 animate-spin" aria-hidden /> : icon}
      {children}
    </button>
  );
});

// ---------------------------------------------------------------------------
// Form field
// ---------------------------------------------------------------------------
export const Field = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement> & { label: string; hint?: string; error?: string; trailing?: ReactNode }
>(function Field({ label, hint, error, trailing, id, className, ...rest }, ref) {
  const fieldId = id ?? `f-${label.toLowerCase().replace(/\W+/g, '-')}`;
  return (
    <div className={clsx('flex flex-col gap-1.5', className)}>
      <label htmlFor={fieldId} className="text-sm font-semibold text-ink">
        {label}
      </label>
      <div className="relative">
        <input
          ref={ref}
          id={fieldId}
          aria-invalid={!!error}
          aria-describedby={error ? `${fieldId}-err` : hint ? `${fieldId}-hint` : undefined}
          className={clsx(
            'h-12 w-full rounded-xl border-2 bg-surface px-4 text-base text-ink outline-none transition-colors placeholder:text-muted/70',
            error ? 'border-coral' : 'border-line focus:border-brand',
            trailing && 'pr-20',
          )}
          {...rest}
        />
        {trailing && <div className="absolute inset-y-0 right-2 flex items-center">{trailing}</div>}
      </div>
      {error ? (
        <p id={`${fieldId}-err`} className="text-sm font-medium text-coral">
          {error}
        </p>
      ) : hint ? (
        <p id={`${fieldId}-hint`} className="text-sm text-muted">
          {hint}
        </p>
      ) : null}
    </div>
  );
});

// ---------------------------------------------------------------------------
// Surfaces and small pieces
// ---------------------------------------------------------------------------
export function Card({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={clsx('rounded-[var(--radius-card)] bg-surface shadow-[var(--shadow-card)]', className)}>{children}</div>;
}

export function Logo({ className }: { className?: string }) {
  return <img src="/brand/logo.png" alt="AI4Skool" className={clsx('h-8 w-auto', className)} />;
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <div role="status" className="flex items-center justify-center gap-3 py-16 text-muted">
      <Loader2 className="size-6 animate-spin text-brand" aria-hidden />
      <span className="font-medium">{label}…</span>
    </div>
  );
}

export function Notice({ tone = 'info', children }: { tone?: 'info' | 'warn' | 'error'; children: ReactNode }) {
  const tones = {
    info: 'bg-brand-soft text-brand-strong',
    warn: 'bg-gold-soft text-[#8a5a06]',
    error: 'bg-coral-soft text-[#a23e25]',
  };
  return (
    <div role={tone === 'error' ? 'alert' : 'status'} className={clsx('rounded-xl px-4 py-3 text-sm font-medium', tones[tone])}>
      {children}
    </div>
  );
}

export function Stars({ earned, max = 3, size = 'md' }: { earned: number; max?: number; size?: 'sm' | 'md' | 'lg' }) {
  const px = { sm: 'size-4', md: 'size-6', lg: 'size-10' }[size];
  return (
    <span className="inline-flex items-center gap-0.5" role="img" aria-label={`${earned} of ${max} stars`}>
      {Array.from({ length: max }, (_, i) => (
        <Star
          key={i}
          className={clsx(px, i < earned ? 'fill-gold text-gold' : 'fill-line text-line')}
          strokeWidth={1.5}
          aria-hidden
        />
      ))}
    </span>
  );
}

/**
 * Class 3 rule: every child-facing text can be read aloud on tap.
 * Uses the recorded narrator line when there is one, device speech otherwise.
 */
export function ReadAloud({ text, src, className }: { text: string; src?: string; className?: string }) {
  const [speaking, setSpeaking] = useState(false);
  const supported = !!src || (typeof window !== 'undefined' && 'speechSynthesis' in window);
  useEffect(() => () => stopVoice(), []);
  if (!supported) return null;
  const speak = () => {
    setSpeaking(true);
    void playVoice(src, text).finally(() => setSpeaking(false));
  };
  return (
    <button
      type="button"
      onClick={speak}
      aria-label="Read aloud"
      className={clsx(
        'inline-flex size-11 shrink-0 items-center justify-center rounded-full transition-colors',
        speaking ? 'bg-brand text-white' : 'bg-brand-soft text-brand hover:bg-brand hover:text-white',
        className,
      )}
    >
      <Volume2 className="size-5" aria-hidden />
    </button>
  );
}
